import { useState } from 'react';
import {
  Stack, Paper, Group, Text, Button, Table, Modal, TextInput, Select,
  Switch, Tabs, Skeleton, ActionIcon, Badge,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconUsers, IconCheck, IconX, IconUserPlus, IconShieldCheck,
} from '@tabler/icons-react';
import PageHeader from '@/components/PageHeader';
import EmptyState from '@/components/EmptyState';
import StatusBadge from '@/components/StatusBadge';
import {
  useSubUsers, useInviteSubUser, useUpdateSubUser, useDeleteSubUser,
  usePendingApprovals, useApproveOrder, useRejectOrder,
} from '@/hooks/usePortalApi';
import type { SubUser } from '@/types';

const AVAILABLE_ROLES = [
  { value: 'admin', label: 'Administrator' },
  { value: 'buyer', label: 'Buyer' },
  { value: 'viewer', label: 'Viewer' },
];

export default function TeamManagement() {
  const [activeTab, setActiveTab] = useState<string | null>('team');
  const [inviteModalOpened, { open: openInviteModal, close: closeInviteModal }] = useDisclosure(false);
  const [editingUser, setEditingUser] = useState<SubUser | null>(null);

  // Form state for invite modal
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<string>('buyer');
  const [inviteCanPlaceOrders, setInviteCanPlaceOrders] = useState(true);
  const [inviteCanMakePayments, setInviteCanMakePayments] = useState(false);

  // API hooks
  const { data: subUsersData, isLoading: loadingSubUsers } = useSubUsers();
  const subUsers = subUsersData?.items ?? [];
  const { data: pendingApprovalsData, isLoading: loadingApprovals } = usePendingApprovals();
  const pendingApprovals = pendingApprovalsData?.items ?? [];
  const inviteSubUser = useInviteSubUser();
  const updateSubUser = useUpdateSubUser();
  const deleteSubUser = useDeleteSubUser();
  const approveOrder = useApproveOrder();
  const rejectOrder = useRejectOrder();

  const handleInviteUser = async () => {
    if (!inviteEmail || !inviteRole) {
      notifications.show({
        title: 'Validation Error',
        message: 'Email and role are required',
        color: 'red',
        icon: <IconX size={16} />,
      });
      return;
    }

    try {
      await inviteSubUser.mutateAsync({
        username: inviteEmail.split('@')[0],
        email: inviteEmail,
        role: inviteRole,
        can_place_orders: inviteCanPlaceOrders,
        can_make_payments: inviteCanMakePayments,
      });

      notifications.show({
        title: 'User Invited',
        message: `Invitation sent to ${inviteEmail}`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      // Reset form
      setInviteEmail('');
      setInviteRole('buyer');
      setInviteCanPlaceOrders(true);
      setInviteCanMakePayments(false);
      closeInviteModal();
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to invite user',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleUpdateUser = async (userId: number, updates: Partial<SubUser>) => {
    try {
      await updateSubUser.mutateAsync({
        id: userId,
        role: updates.role,
        can_place_orders: updates.can_place_orders,
        can_make_payments: updates.can_make_payments,
        is_active: updates.is_active,
      });

      notifications.show({
        title: 'User Updated',
        message: 'User settings have been updated',
        color: 'green',
        icon: <IconCheck size={16} />,
      });

      setEditingUser(null);
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to update user',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleDeactivateUser = async (userId: number, userName: string) => {
    if (!confirm(`Are you sure you want to deactivate ${userName}?`)) {
      return;
    }

    try {
      await deleteSubUser.mutateAsync(userId);

      notifications.show({
        title: 'User Deactivated',
        message: `${userName} has been deactivated`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to deactivate user',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleApproveOrder = async (orderId: number, orderNumber: string) => {
    try {
      await approveOrder.mutateAsync(orderId);

      notifications.show({
        title: 'Order Approved',
        message: `Order ${orderNumber} has been approved`,
        color: 'green',
        icon: <IconCheck size={16} />,
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to approve order',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const handleRejectOrder = async (orderId: number, orderNumber: string) => {
    const reason = prompt('Reason for rejection (optional):');
    if (reason === null) return;

    try {
      await rejectOrder.mutateAsync({ id: orderId, reason: reason || undefined });

      notifications.show({
        title: 'Order Rejected',
        message: `Order ${orderNumber} has been rejected`,
        color: 'orange',
        icon: <IconX size={16} />,
      });
    } catch {
      notifications.show({
        title: 'Error',
        message: 'Failed to reject order',
        color: 'red',
        icon: <IconX size={16} />,
      });
    }
  };

  const renderTeamMembersTab = () => {
    if (loadingSubUsers) {
      return (
        <Stack gap="md">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} height={60} />
          ))}
        </Stack>
      );
    }

    if (subUsers.length === 0) {
      return (
        <EmptyState
          icon={<IconUsers size={48} />}
          title="No team members yet"
          description="Invite users to collaborate on orders and manage your account"
          action={
            <Button leftSection={<IconUserPlus size={16} />} onClick={openInviteModal}>
              Invite User
            </Button>
          }
        />
      );
    }

    return (
      <Stack gap="md">
        <Group justify="space-between">
          <Text size="sm" c="dimmed">
            {subUsers.length} team member{subUsers.length !== 1 ? 's' : ''}
          </Text>
          <Button leftSection={<IconUserPlus size={16} />} onClick={openInviteModal}>
            Invite User
          </Button>
        </Group>

        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th>Email</Table.Th>
                <Table.Th>Role</Table.Th>
                <Table.Th>Capabilities</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {subUsers.map((user) => (
                <Table.Tr key={user.id}>
                  <Table.Td>
                    <Text fw={500}>{user.username}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{user.email}</Text>
                  </Table.Td>
                  <Table.Td>
                    {editingUser?.id === user.id ? (
                      <Select
                        data={AVAILABLE_ROLES}
                        value={editingUser.role}
                        onChange={(value) =>
                          setEditingUser({ ...editingUser, role: (value as SubUser['role']) || 'buyer' })
                        }
                        size="xs"
                        style={{ width: 150 }}
                      />
                    ) : (
                      <Badge variant="light" color="blue">
                        {user.role}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {editingUser?.id === user.id ? (
                      <Stack gap="xs">
                        <Switch
                          label="Place orders"
                          size="xs"
                          checked={editingUser.can_place_orders}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, can_place_orders: e.currentTarget.checked })
                          }
                        />
                        <Switch
                          label="Make payments"
                          size="xs"
                          checked={editingUser.can_make_payments}
                          onChange={(e) =>
                            setEditingUser({ ...editingUser, can_make_payments: e.currentTarget.checked })
                          }
                        />
                      </Stack>
                    ) : (
                      <Group gap={4}>
                        {user.can_place_orders && (
                          <Badge size="xs" variant="outline">Orders</Badge>
                        )}
                        {user.can_make_payments && (
                          <Badge size="xs" variant="outline">Payments</Badge>
                        )}
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <StatusBadge status={user.is_active ? 'active' : 'inactive'} />
                  </Table.Td>
                  <Table.Td>
                    <Group gap="xs">
                      {editingUser?.id === user.id ? (
                        <>
                          <ActionIcon
                            color="green"
                            variant="light"
                            onClick={() =>
                              handleUpdateUser(user.id, {
                                role: editingUser.role,
                                can_place_orders: editingUser.can_place_orders,
                                can_make_payments: editingUser.can_make_payments,
                              })
                            }
                            loading={updateSubUser.isPending}
                          >
                            <IconCheck size={16} />
                          </ActionIcon>
                          <ActionIcon
                            color="gray"
                            variant="light"
                            onClick={() => setEditingUser(null)}
                          >
                            <IconX size={16} />
                          </ActionIcon>
                        </>
                      ) : (
                        <>
                          <Button
                            size="xs"
                            variant="light"
                            onClick={() => setEditingUser(user)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="xs"
                            variant="light"
                            color="red"
                            onClick={() => handleDeactivateUser(user.id, user.username || user.email)}
                            loading={deleteSubUser.isPending}
                            disabled={!user.is_active}
                          >
                            Deactivate
                          </Button>
                        </>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      </Stack>
    );
  };

  const renderPendingApprovalsTab = () => {
    if (loadingApprovals) {
      return (
        <Stack gap="md">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} height={100} />
          ))}
        </Stack>
      );
    }

    if (pendingApprovals.length === 0) {
      return (
        <EmptyState
          icon={<IconShieldCheck size={48} />}
          title="No pending approvals"
          description="Orders requiring approval will appear here"
        />
      );
    }

    return (
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {pendingApprovals.length} order{pendingApprovals.length !== 1 ? 's' : ''} pending approval
        </Text>

        {pendingApprovals.map((approval) => (
          <Paper key={approval.id} withBorder p="md">
            <Group justify="space-between" align="flex-start">
              <Stack gap="xs" style={{ flex: 1 }}>
                <Group gap="sm">
                  <Text fw={600} size="lg">
                    Order #{approval.order_number}
                  </Text>
                  <Badge color="yellow">Pending Approval</Badge>
                </Group>

                <Group gap="xl">
                  <div>
                    <Text size="xs" c="dimmed">
                      Requested by
                    </Text>
                    <Text size="sm" fw={500}>
                      {approval.requested_by}
                    </Text>
                  </div>

                  <div>
                    <Text size="xs" c="dimmed">
                      Amount
                    </Text>
                    <Text size="sm" fw={600}>
                      ${(approval.total ?? 0).toFixed(2)}
                    </Text>
                  </div>

                  <div>
                    <Text size="xs" c="dimmed">
                      Date
                    </Text>
                    <Text size="sm">
                      {approval.created_at
                        ? new Date(approval.created_at).toLocaleDateString()
                        : 'N/A'}
                    </Text>
                  </div>
                </Group>

                {approval.notes && (
                  <div>
                    <Text size="xs" c="dimmed">
                      Notes
                    </Text>
                    <Text size="sm">{approval.notes}</Text>
                  </div>
                )}
              </Stack>

              <Group gap="sm">
                <Button
                  leftSection={<IconCheck size={16} />}
                  color="green"
                  onClick={() => handleApproveOrder(approval.order_id, approval.order_number)}
                  loading={approveOrder.isPending}
                >
                  Approve
                </Button>
                <Button
                  leftSection={<IconX size={16} />}
                  color="red"
                  variant="light"
                  onClick={() => handleRejectOrder(approval.order_id, approval.order_number)}
                  loading={rejectOrder.isPending}
                >
                  Reject
                </Button>
              </Group>
            </Group>
          </Paper>
        ))}
      </Stack>
    );
  };

  return (
    <Stack gap="lg">
      <PageHeader
        title="Team Management"
        description="Manage team members and approve orders"
      />

      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="team" leftSection={<IconUsers size={16} />}>
            Team Members
          </Tabs.Tab>
          <Tabs.Tab
            value="approvals"
            leftSection={<IconShieldCheck size={16} />}
            rightSection={
              pendingApprovals.length > 0 ? (
                <Badge size="sm" variant="filled" color="red" circle>
                  {pendingApprovals.length}
                </Badge>
              ) : null
            }
          >
            Pending Approvals
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="team" pt="md">
          {renderTeamMembersTab()}
        </Tabs.Panel>

        <Tabs.Panel value="approvals" pt="md">
          {renderPendingApprovalsTab()}
        </Tabs.Panel>
      </Tabs>

      {/* Invite User Modal */}
      <Modal
        opened={inviteModalOpened}
        onClose={closeInviteModal}
        title="Invite Team Member"
        size="md"
      >
        <Stack gap="md">
          <TextInput
            label="Email Address"
            placeholder="user@company.com"
            required
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.currentTarget.value)}
          />

          <Select
            label="Role"
            placeholder="Select role"
            data={AVAILABLE_ROLES}
            value={inviteRole}
            onChange={(value) => setInviteRole(value || 'buyer')}
            required
          />

          <Stack gap="xs">
            <Text size="sm" fw={500}>Capabilities</Text>
            <Switch
              label="Can place orders"
              checked={inviteCanPlaceOrders}
              onChange={(e) => setInviteCanPlaceOrders(e.currentTarget.checked)}
            />
            <Switch
              label="Can make payments"
              checked={inviteCanMakePayments}
              onChange={(e) => setInviteCanMakePayments(e.currentTarget.checked)}
            />
          </Stack>

          <Group justify="flex-end" mt="md">
            <Button variant="light" onClick={closeInviteModal}>
              Cancel
            </Button>
            <Button
              leftSection={<IconUserPlus size={16} />}
              onClick={handleInviteUser}
              loading={inviteSubUser.isPending}
            >
              Send Invitation
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
